import React, { useState, useMemo } from 'react';
import {
    Eye,
    Download,
    BarChart3,
    ChevronLeft,
    ChevronRight,
    X
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCurrency, formatDate, holdingsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// NOTE: This component was renamed from SukukHoldings to BondHoldings to align with updated terminology.
// Mixed Islamic vs Conventional bond handling is preserved; domain-specific labels ("Sukuk") still appear
// where they distinguish Islamic instruments.
const BondHoldings = () => {
    const { getDisplayName, user } = useAuth();
    const queryClient = useQueryClient();
    const [selectedBond, setSelectedBond] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Fetch holdings from the holdings table
    const { data: investorHoldingsData = [], isPending: holdingsLoading, error: holdingsError } = useQuery({
        queryKey: ['investor-my-holdings', user?.id],
        queryFn: async () => {
            const resp = await holdingsAPI.getMyHoldings({ status: 'all' });
            const root = resp?.data || resp;
            if (Array.isArray(root?.data)) return root.data;
            if (Array.isArray(root)) return root;
            return [];
        },
        enabled: !!user?.id,
        staleTime: 60_000
    });

    // Filter and process holdings - exclude inactive holdings and Primary Subscriber direct holdings
    const rawHoldings = useMemo(() => {
        if (!Array.isArray(investorHoldingsData) || investorHoldingsData.length === 0) return [];

        return investorHoldingsData.filter(holding => {
            // Exclude inactive holdings
            if ((holding.remainingTokenAmount || 0) <= 0 || holding.holdingsEndDate) {
                return false;
            }

            // Exclude Primary Subscriber direct holdings (where holder is PS)
            const holderSnapshot = holding.holderSnapshot || {};
            const isPrimarySubscriberHolder = holderSnapshot.isPrimarySubscriber === true;

            if (isPrimarySubscriberHolder) {
                return false;
            }

            return true;
        });
    }, [investorHoldingsData]);

    // Aggregate holdings by bond and normalize data structure
    const confirmedHoldings = useMemo(() => {
        if (!Array.isArray(rawHoldings) || rawHoldings.length === 0) return [];

        // Group holdings by bondId to aggregate amounts
        const holdingsByBond = new Map();

        rawHoldings.forEach((holding, idx) => {
            const bondIdRaw = typeof holding?.bondId === 'object' && holding?.bondId !== null
                ? holding.bondId._id || holding.bondId.id || holding.bondId
                : holding?.bondId;
            const bondIdKey = bondIdRaw ? String(bondIdRaw) : null;

            if (!bondIdKey) return; // Skip holdings without bondId

            const bondSnapshot = holding.bondSnapshot || {};
            const holderSnapshot = holding.holderSnapshot || {};
            const investorSnapshot = holding.investorSnapshot || {};

            // Determine allocation type based on source
            const custodianId = holding.custodianId?._id || holding.custodianId;
            const primarySubscriberId = holding.primarySubscriberId?._id || holding.primarySubscriberId;
            let allocationType = 'direct';
            if (custodianId) {
                allocationType = 'custodian';
            } else if (primarySubscriberId) {
                allocationType = 'primary_subscriber';
            }

            const productType = bondSnapshot.productType || '';
            const islamic = /sukuk|islamic/i.test(productType.toLowerCase());

            const profitRate = bondSnapshot.profitRate ?? null;
            const couponRate = bondSnapshot.couponRate ?? null;

            const tokenAmount = holding.remainingTokenAmount || 0;
            const currentValue = tokenAmount; // Holdings already represent current holdings

            // Extract issuer info - bondSnapshot.issuer is stored as a string
            const issuer = bondSnapshot.issuer || '—';

            if (holdingsByBond.has(bondIdKey)) {
                // Aggregate holdings for the same bond
                const existing = holdingsByBond.get(bondIdKey);
                existing.amount += tokenAmount;
                existing.currentValue += currentValue;
            } else {
                // Create new bond holding entry
                holdingsByBond.set(bondIdKey, {
                    id: holding._id || idx,
                    bondId: bondIdKey,
                    bondName: bondSnapshot.bondName || 'Unnamed Bond',
                    isin: bondSnapshot.isin || '—',
                    stockCode: bondSnapshot.stockCode || '—',
                    bondType: islamic ? 'Islamic' : 'Traditional',
                    amount: tokenAmount,
                    currentValue,
                    maturityDate: bondSnapshot.maturityDate || null,
                    profitRate,
                    couponRate,
                    status: 'active', // Holdings are active if they passed the filter
                    issuer,
                    issueDate: bondSnapshot.issueDate || holding.createdAt || new Date().toISOString(),
                    paymentFrequency: '—', // Not in bondSnapshot, can be added if needed
                    nextPaymentDate: null,
                    nextPaymentAmount: null,
                    totalPaymentsReceived: 0,
                    marketPrice: 100,
                    yieldToMaturity: 0,
                    creditRating: '—',
                    sector: '—',
                    currency: 'MYR',
                    allocationType
                });
            }
        });

        return Array.from(holdingsByBond.values());
    }, [rawHoldings]);

    // Holdings data for display
    const holdingsData = useMemo(() => {
        return confirmedHoldings.map((holding) => ({
            ...holding,
            type: 'holding',
        }));
    }, [confirmedHoldings]);

    const totalItems = holdingsData.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const currentPage = Math.min(page, totalPages);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const displayedHoldings = holdingsData.slice(startIndex, endIndex);

    const isLoading = holdingsLoading;
    const loadError = holdingsError || null;

    // Summary widgets removed per request; keyMetrics disabled.
    const keyMetrics = [];

    const getStatusBadge = (status) => {
        const normalized = (status || '').toLowerCase();
        const statusConfig = {
            active: { label: 'Active', class: 'badge-success' },
            matured: { label: 'Matured', class: 'badge-secondary' },
            suspended: { label: 'Suspended', class: 'badge-warning' },
            pending_l1: { label: 'Pending L1', class: 'badge-warning whitespace-nowrap' },
            pending_l2: { label: 'Pending L2', class: 'badge-info whitespace-nowrap' },
            submitted: { label: 'Submitted', class: 'badge-info' },
            approved: { label: 'Approved', class: 'badge-success' },
            allocated: { label: 'Allocated', class: 'badge-success' },
            rejected: { label: 'Rejected', class: 'bg-red-100 text-red-800' },
        };
        const config = statusConfig[normalized] || { label: status, class: 'badge-secondary' };
        return <span className={`badge ${config.class}`}>{config.label}</span>;
    };

    const getApprovalStatusBadge = (request) => {
        if (!request) return null;

        // Check if rejected
        if (request.rejectedBy) {
            return (
                <span className="text-red-800 bg-red-100 badge">
                    Rejected
                </span>
            );
        }

        // Check status progression: pending_l1 -> pending_l2 -> submitted (completed)
        // L2 approved (both L1 and L2 approved) - Final state
        if (request.l2ApprovedBy) {
            return (
                <span className="badge badge-success">
                    Submitted
                </span>
            );
        }

        // L1 approved, waiting for L2 - Pending L2 state
        if (request.l1ApprovedBy) {
            return (
                <span className="badge badge-info">
                    Pending L2
                </span>
            );
        }

        // Not yet approved - Pending L1 state
        return (
            <span className="badge badge-warning">
                Pending L1
            </span>
        );
    };

    const getCreditRatingBadge = (rating) => {
        const ratingConfig = {
            'AAA': { class: 'badge-success' },
            'AA+': { class: 'badge-info' },
            'AA': { class: 'badge-info' },
            'A': { class: 'badge-warning' },
            'BBB': { class: 'badge-secondary' }
        };
        const config = ratingConfig[rating] || { class: 'badge-secondary' };
        return <span className={`badge ${config.class}`}>{rating}</span>;
    };

    const getBondTypeDisplay = (bondType) => {
        return bondType === 'Islamic' ? 'Sukuk' : 'Conventional Bond';
    };

    const getRateLabel = (bondType) => {
        return bondType === 'Islamic' ? 'Profit Rate' : 'Coupon Rate';
    };

    const getRateValue = (holding) => {
        if (holding.bondType === 'Islamic') {
            return holding.profitRate != null ? holding.profitRate : holding.couponRate;
        }
        return holding.couponRate != null ? holding.couponRate : holding.profitRate;
    };

    const getPaymentTypeLabel = (type, bondType) => {
        if (type === 'profit_distribution') {
            return bondType === 'Islamic' ? 'Profit Distribution' : 'Interest Payment';
        }
        return 'Coupon Payment';
    };

    const handleViewDetails = (holding) => {
        setSelectedBond(holding);
        setShowDetails(true);
    };

    if (isLoading) {
        return <LoadingSpinner size="lg" text="Loading bond holdings..." />;
    }

    if (loadError) {
        return (
            <div className="card">
                <div className="card-body">
                    <h3 className="mb-2 text-lg font-medium text-error-600">Failed to load bond holdings</h3>
                    <p className="mb-4 text-sm text-secondary-600">{loadError.message || loadError.toString()}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div className="space-y-6">
                    {/* Page Header */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Bond Holdings</h1>
                            <p className="mt-1 text-secondary-600">
                                View your current bond holdings
                            </p>
                        </div>
                    </div>

                    {/* Holdings Table */}
                    <div className="card">
                        <div className="card-header">
                            <div className="flex items-center justify-between w-full">
                                <h3>My Holdings</h3>
                                <button className="btn-primary btn-sm">
                                    <Download className="w-4 h-4 mr-2" />
                                    Export
                                </button>
                            </div>
                        </div>
                        <div className="p-0 card-body">
                            <div className="overflow-x-auto">
                                <table className="table">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="table-header-cell">Bond Name</th>
                                            <th className="table-header-cell">ISIN</th>
                                            <th className="table-header-cell">Stock Code</th>
                                            <th className="table-header-cell">Amount</th>
                                            <th className="table-header-cell">Current Value</th>
                                            <th className="table-header-cell">Rate</th>
                                            <th className="table-header-cell">Maturity</th>
                                            <th className="table-header-cell">Status</th>
                                            <th className="table-header-cell">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedHoldings.map((holding) => {
                                            const rateValue = getRateValue(holding);
                                            return (
                                                <tr key={holding.id} className="transition-colors border-b border-light-gray hover:bg-secondary-50">
                                                    <td className="px-4 py-4">
                                                        <div>
                                                            <div className="text-sm font-semibold leading-5 text-dark-gray-900">
                                                                {holding.bondName}
                                                            </div>
                                                            <div className="mt-1 text-xs text-secondary-500">
                                                                {holding.issuer} | {getBondTypeDisplay(holding.bondType)}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 font-mono text-sm text-secondary-600">
                                                        {holding.isin || '—'}
                                                    </td>
                                                    <td className="px-4 py-4 font-mono text-sm text-secondary-600">
                                                        {holding.stockCode || '—'}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-secondary-600">
                                                        {formatCurrency(holding.amount)}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-secondary-600">
                                                        {formatCurrency(holding.currentValue)}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-secondary-600">
                                                        {rateValue != null ? `${rateValue}%` : '—'}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-secondary-600">
                                                        {holding.maturityDate ? formatDate(holding.maturityDate) : '—'}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        {getStatusBadge(holding.status)}
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <button
                                                            onClick={() => handleViewDetails(holding)}
                                                            className="inline-flex items-center justify-center w-8 h-8 transition-colors rounded-lg hover:bg-secondary-200 text-primary-600 hover:text-secondary-700"
                                                            title="View Details"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {!holdingsData.length && (
                                            <tr>
                                                <td colSpan="9" className="py-12 text-center text-secondary-500">
                                                    <div className="flex flex-col items-center space-y-2">
                                                        <BarChart3 className="w-10 h-10 text-gray-300" />
                                                        <p className="text-sm">No bond holdings yet</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                {holdingsData.length > 0 && (
                                    <div className="px-6 py-3 border-t bg-secondary-50 border-light-gray">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center text-sm text-secondary-500">
                                                <span>
                                                    Showing {holdingsData.length === 0 ? 0 : startIndex + 1} to{' '}
                                                    {Math.min(endIndex, totalItems)} of{' '}
                                                    {totalItems} results
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                                    disabled={currentPage === 1}
                                                    className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                </button>

                                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setPage(pageNum)}
                                                        className={`px-3 py-1 rounded-md text-sm font-medium ${pageNum === currentPage
                                                            ? 'bg-primary-600 text-white'
                                                            : 'bg-white border border-secondary-300 text-secondary-700 hover:bg-secondary-50'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                ))}

                                                <button
                                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                                    disabled={currentPage === totalPages}
                                                    className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <ChevronRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Bond Details Modal */}
                    {showDetails && selectedBond && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
                                <div className="p-6 pb-0">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-medium text-gray-900">Bond Details</h3>
                                        <button
                                            onClick={() => setShowDetails(false)}
                                            className="w-6 h-6 text-gray-400 hover:text-gray-600"
                                            style={{ color: '#9ca3af' }}
                                        >
                                            <X className="w-6 h-6" />
                                        </button>
                                    </div>
                                </div>
                                <div className="w-full mt-4 -mx-0 border-b border-light-gray"></div>
                                <div className="p-6 pt-6">

                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        {/* Basic Information */}
                                        <div className="space-y-4">
                                            <h4 className="mb-2 text-sm font-medium tracking-wider text-gray-500 uppercase">Basic Information</h4>
                                            <div className="space-y-3">
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">Bond Name:</span>
                                                    <span className="text-sm text-secondary-900">{selectedBond.bondName}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">ISIN:</span>
                                                    <span className="font-mono text-sm text-secondary-900">{selectedBond.isin}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">Bond Type:</span>
                                                    <span className="text-sm text-secondary-900">{getBondTypeDisplay(selectedBond.bondType)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">Issuer:</span>
                                                    <span className="text-sm text-secondary-900">{selectedBond.issuer}</span>
                                                </div>
                                            
                                            </div>
                                        </div>

                                        {/* Investment Details */}
                                        <div className="space-y-4">
                                            <h4 className="mb-2 text-sm font-medium tracking-wider text-gray-500 uppercase">Investment Details</h4>
                                            <div className="space-y-3">
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">Amount Held:</span>
                                                    <span className="text-sm text-secondary-900">{formatCurrency(selectedBond.amount)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">Current Value:</span>
                                                    <span className="text-sm text-secondary-900">{formatCurrency(selectedBond.currentValue)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">{getRateLabel(selectedBond.bondType)}:</span>
                                                    <span className="text-sm text-secondary-900">{getRateValue(selectedBond)}%</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">Market Price:</span>
                                                    <span className="text-sm text-secondary-900">{selectedBond.marketPrice}%</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">Yield to Maturity:</span>
                                                    <span className="text-sm text-secondary-900">{selectedBond.yieldToMaturity}%</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Maturity & Payments */}
                                        <div className="space-y-4">
                                            <h4 className="mb-2 text-sm font-medium tracking-wider text-gray-500 uppercase">Maturity & Payments</h4>
                                            <div className="space-y-3">
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">Maturity Date:</span>
                                                    <span className="text-sm text-secondary-900">{formatDate(selectedBond.maturityDate)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-medium text-dark-gray">Issue Date:</span>
                                                    <span className="text-sm text-secondary-900">{formatDate(selectedBond.issueDate)}</span>
                                                </div>
                                            
                                            </div>
                                        </div>

                                    
                                    </div>

                             
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BondHoldings;
